/* eslint-disable jsx-a11y/anchor-is-valid */
import { FC, useEffect } from 'react'
import { useMutation, useQueryClient } from 'react-query'
import { MenuComponent } from '../../../../../../_metronic/assets/ts/components'
import { ID, KTSVG, QUERIES } from '../../../../../../_metronic/helpers'
import { useListView } from '../../core/ListViewProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { deleteBalance, rollbackBalance } from '../../core/_requests'
import { FormattedMessage } from 'react-intl'
import Swal from 'sweetalert2'
import toast from 'react-hot-toast'

type Props = {
  id: ID
  status: string  // 👈 Include status field
}

const BalanceActionsCell: FC<Props> = ({ id, status }) => {
  const { setItemIdForUpdate } = useListView()
  const { query } = useQueryResponse()
  const queryClient = useQueryClient()

  useEffect(() => {
    MenuComponent.reinitialization()
  }, [])

  const openEditModal = () => {
    setItemIdForUpdate(id)
  }

  // ✅ Mutation for deleting balance
  const deleteItem = useMutation(() => deleteBalance(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([`${QUERIES.USERS_LIST}-${query}`])
      toast.success('Balance deleted successfully', { position: 'top-right' })
    },
    onError: () => {
      toast.error('Failed to delete balance', { position: 'top-right' })
    },
  })

  // ✅ Mutation for rolling back balance
  const rollbackItem = useMutation(() => rollbackBalance(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([`${QUERIES.USERS_LIST}-${query}`])
      toast.success('Balance rollbacked successfully', { position: 'top-right' })
    },
    onError: () => {
      toast.error('Failed to rollback balance', { position: 'top-right' })
    },
  })

  // 🔹 Confirm before deleting balance
  const confirmDelete = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this balance record!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    })

    if (result.isConfirmed) {
      deleteItem.mutate()
    }
  }

  // 🔹 Confirm before rollback
  const confirmRollback = async () => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You want to rollback this balance transaction!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, rollback it!',
    })

    if (result.isConfirmed) {
      rollbackItem.mutate()
    }
  }

  return (
    <>
      <a
        href='#'
        className='btn btn-light btn-active-light-primary btn-sm'
        data-kt-menu-trigger='click'
        data-kt-menu-placement='bottom-end'
      >
        <FormattedMessage id='TABLE.GENERAL.ACTION' />
        <KTSVG path='/media/icons/duotune/arrows/arr072.svg' className='svg-icon-5 m-0' />
      </a>

      {/* begin::Menu */}
      <div
        className='menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-600 menu-state-bg-light-primary fw-bold fs-7 w-125px py-4'
        data-kt-menu='true'
      >
        {/* Edit Option */}
        <div className='menu-item px-3'>
          <a className='menu-link px-3' onClick={openEditModal}>
            <FormattedMessage id='TABLE.GENERAL.EDIT' />
          </a>
        </div>

        {/* Delete Option */}
        <div className='menu-item px-3'>
          <a className='menu-link px-3' onClick={async () => await confirmDelete()}>
            <FormattedMessage id='TABLE.GENERAL.DELETE' />
          </a>
        </div>

        {/* Rollback Option (only if not rollbacked) */}
        {status !== 'rollbacked' ? (
          <div className='menu-item px-3'>
            <a className='menu-link px-3' onClick={async () => await confirmRollback()}>
              <FormattedMessage id='TABLE.GENERAL.ROLLBACK' defaultMessage={"بازگشت / کنسل"} />
            </a>
          </div>
        ) : (
          // Show "Remove" instead of "Rollback" if already rollbacked
          <div className='menu-item px-3'>
            <a className='menu-link px-3' onClick={async () => await confirmDelete()}>
              <FormattedMessage id='TABLE.GENERAL.REMOVE' />
            </a>
          </div>
        )}
      </div>
      {/* end::Menu */}
    </>
  )
}

export { BalanceActionsCell }
